/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from './AuthContext';
import { tokenStorage } from '../utils/storage';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user, isAuth } = useAuth();
  const [connection, setConnection] = useState(null);
  const [connected, setConnected] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeMessages, setActiveMessages] = useState({});
  const listeners = useRef({});
  const isConnectingRef = useRef(false);

  useEffect(() => {
    if (!isAuth || !user) return;

    const useHosted = import.meta.env.VITE_USE_HOSTED_API === 'true';

    const isLocalHost = (hostname) => {
      return (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === '10.0.2.2' ||
        hostname.startsWith('192.168.') ||
        hostname.startsWith('172.') ||
        hostname.startsWith('10.')
      );
    };

    const isLocal = !useHosted && isLocalHost(window.location.hostname);
    const hubUrl = isLocal
      ? `http://${window.location.hostname}:5000/hubs/chat`
      : 'https://shopaxo.in/API/hubs/chat';

    // Infinite retry policy to prevent connection dropping completely (stops on 401 Unauthorized)
    const infiniteRetryPolicy = {
      nextRetryDelayInMilliseconds: (retryContext) => {
        const err = retryContext.retryReason;
        const isUnauthorized = err?.statusCode === 401 ||
          (err?.message && (err.message.includes('401') || err.message.includes('Unauthorized')));

        if (isUnauthorized) {
          console.warn('[SignalR] Automatic reconnect stopped due to 401 Unauthorized authentication failure.');
          return null;
        }

        const delays = [0, 1000, 2000, 5000, 10000, 15000];
        const count = retryContext.previousRetryCount;
        return count < delays.length ? delays[count] : 15000;
      }
    };

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => (await tokenStorage.getItem('accessToken')) || '',
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect(infiniteRetryPolicy)
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Broadcast event dispatcher to registered listeners
    const dispatchEvent = (eventName, data) => {
      Object.values(listeners.current).forEach(fn => {
        try {
          fn(eventName, data);
        } catch (e) {
          console.error(`[SignalR Listener Error] ${eventName}:`, e);
        }
      });
    };

    // Chat events
    conn.on('ReceiveMessage', (message) => {
      const convId = message.conversationId;
      setActiveMessages(prev => ({
        ...prev,
        [convId]: [...(prev[convId] || []), { ...message, isMine: message.senderId === user.userId }]
      }));

      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, lastMessage: message, updatedAt: message.createdAt } : c
      ));

      dispatchEvent('ReceiveMessage', message);
    });

    conn.on('MessageEdited', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => m.id === data.messageId ? { ...m, content: data.newContent } : m)
        };
      });
      dispatchEvent('MessageEdited', data);
    });

    conn.on('MessageDeleted', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => m.id === data.messageId ? { ...m, isDeleted: true, content: '🚫 This message was deleted' } : m)
        };
      });
      dispatchEvent('MessageDeleted', data);
    });

    conn.on('MessageReacted', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => m.id === data.messageId ? { ...m, reactions: data.reactions } : m)
        };
      });
      dispatchEvent('MessageReacted', data);
    });

    conn.on('UserTyping', (data) => {
      dispatchEvent('UserTyping', data);
    });

    conn.on('MessagesDelivered', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => {
            if (m.senderId !== data.userId && m.status === 'Sent') {
              return { ...m, status: 'Delivered' };
            }
            return m;
          })
        };
      });

      setConversations(prev => prev.map(c => {
        if (c.id === data.conversationId && c.lastMessage && c.lastMessage.senderId !== data.userId && c.lastMessage.status === 'Sent') {
          return { ...c, lastMessage: { ...c.lastMessage, status: 'Delivered' } };
        }
        return c;
      }));

      dispatchEvent('MessagesDelivered', data);
    });

    conn.on('MessageRead', (data) => {
      setActiveMessages(prev => {
        const list = prev[data.conversationId] || [];
        return {
          ...prev,
          [data.conversationId]: list.map(m => {
            if (m.senderId !== data.userId && (m.status === 'Sent' || m.status === 'Delivered')) {
              return { ...m, status: 'Read' };
            }
            return m;
          })
        };
      });

      setConversations(prev => prev.map(c => {
        if (c.id === data.conversationId && c.lastMessage && c.lastMessage.senderId !== data.userId && c.lastMessage.status !== 'Read') {
          return { ...c, lastMessage: { ...c.lastMessage, status: 'Read' } };
        }
        return c;
      }));

      dispatchEvent('MessageRead', data);
    });

    conn.on('OrderUpdated', (data) => {
      dispatchEvent('OrderUpdated', data);
    });

    conn.on('ReceiveNotification', (notification) => {
      dispatchEvent('ReceiveNotification', notification);
    });

    conn.on('UserPresenceChanged', (presence) => {
      dispatchEvent('UserPresenceChanged', presence);
    });

    // WebRTC Signaling events
    conn.on('IncomingCall', (callData) => {
      console.log('[SignalR] IncomingCall event received:', callData);
      dispatchEvent('IncomingCall', callData);
    });

    conn.on('ReceiveOffer', (offerData) => {
      console.log('[SignalR] ReceiveOffer event received:', offerData);
      dispatchEvent('ReceiveOffer', offerData);
    });

    conn.on('ReceiveAnswer', (answerData) => {
      console.log('[SignalR] ReceiveAnswer event received:', answerData);
      dispatchEvent('ReceiveAnswer', answerData);
    });

    conn.on('ReceiveIceCandidate', (candidateData) => {
      dispatchEvent('ReceiveIceCandidate', candidateData);
    });

    conn.on('CallAccepted', (acceptData) => {
      console.log('[SignalR] CallAccepted event received:', acceptData);
      dispatchEvent('CallAccepted', acceptData);
    });

    conn.on('CallRejected', (rejectData) => {
      console.log('[SignalR] CallRejected event received:', rejectData);
      dispatchEvent('CallRejected', rejectData);
    });

    conn.on('CallEnded', (endData) => {
      console.log('[SignalR] CallEnded event received:', endData);
      dispatchEvent('CallEnded', endData);
    });

    conn.on('UserBusy', (busyData) => {
      console.log('[SignalR] UserBusy event received:', busyData);
      dispatchEvent('UserBusy', busyData);
    });

    conn.on('CallTimedOut', (timeoutData) => {
      console.log('[SignalR] CallTimedOut event received:', timeoutData);
      dispatchEvent('CallTimedOut', timeoutData);
    });

    // Connection state handlers
    conn.onreconnecting((err) => {
      console.warn('[SignalR] Connection lost, reconnecting...', err);
      setConnected(false);
      dispatchEvent('Reconnecting', err);
    });

    conn.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected successfully. ConnectionId:', connectionId);
      setConnected(true);
      dispatchEvent('Reconnected', connectionId);
    });

    conn.onclose(async (err) => {
      console.warn('[SignalR] Connection closed:', err);
      setConnected(false);
      dispatchEvent('Disconnected', err);
      // Auto-repair if connection closes unexpectedly
      setTimeout(() => startConnection(), 3000);
    });

    const startConnection = async () => {
      if (isConnectingRef.current || conn.state === signalR.HubConnectionState.Connected) return;

      const token = await tokenStorage.getItem('accessToken');
      if (!token) {
        console.warn('[SignalR] No access token found. Halting connection attempt.');
        setConnected(false);
        return;
      }

      try {
        isConnectingRef.current = true;
        await conn.start();
        setConnected(true);
        setConnection(conn);
        console.log('[SignalR] Connection established successfully.');
      } catch (err) {
        const isUnauthorized = err?.statusCode === 401 ||
          (err?.message && (err.message.includes('401') || err.message.includes('Unauthorized')));

        if (isUnauthorized) {
          console.warn('[SignalR] Connection start failed due to 401 Unauthorized. Stopping retry loop until re-login.');
          setConnected(false);
          return;
        }

        console.warn('[SignalR] Connection start failed, retrying in 5s...', err);
        setConnected(false);
        setTimeout(() => startConnection(), 5000);
      } finally {
        isConnectingRef.current = false;
      }
    };

    startConnection();

    // Online/Offline network listeners for instant auto-repair
    const handleOnline = () => {
      console.log('[Network] Browser online detected. Repairing SignalR connection...');
      if (conn.state === signalR.HubConnectionState.Disconnected) {
        startConnection();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[AppLifecycle] App resumed. Checking SignalR state...');
        if (conn.state === signalR.HubConnectionState.Disconnected) {
          startConnection();
        }
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      conn.stop();
      setConnected(false);
    };
  }, [isAuth, user]);

  const sendMessage = useCallback(async (conversationId, content, type = 'Text', refId = null, mediaUrl = null) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('SendMessage', conversationId, content, type, refId, mediaUrl);
  }, [connection]);

  const markRead = useCallback(async (conversationId) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('MarkRead', conversationId);
  }, [connection]);

  const startTyping = useCallback(async (conversationId) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('StartTyping', conversationId);
  }, [connection]);

  const stopTyping = useCallback(async (conversationId) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('StopTyping', conversationId);
  }, [connection]);

  const joinConversation = useCallback(async (conversationId) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('JoinConversation', conversationId);
  }, [connection]);

  // WebRTC SignalR Invokers
  const initiateCall = useCallback(async (targetUserId, conversationId, callType) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR connection is offline.');
    }
    return await connection.invoke('InitiateCall', targetUserId, conversationId, callType);
  }, [connection]);

  const sendOffer = useCallback(async (callId, targetUserId, sdpOffer) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('SendOffer', callId, targetUserId, sdpOffer);
  }, [connection]);

  const sendAnswer = useCallback(async (callId, callerUserId, sdpAnswer) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('SendAnswer', callId, callerUserId, sdpAnswer);
  }, [connection]);

  const sendIceCandidate = useCallback(async (callId, targetUserId, candidateJson) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('SendIceCandidate', callId, targetUserId, candidateJson);
  }, [connection]);

  const acceptCall = useCallback(async (callId, callerUserId) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('AcceptCall', callId, callerUserId);
  }, [connection]);

  const rejectCall = useCallback(async (callId, callerUserId, reason = 'declined') => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('RejectCall', callId, callerUserId, reason);
  }, [connection]);

  const endCall = useCallback(async (callId, targetUserId, durationSeconds = 0, reason = 'user_hung_up') => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
    await connection.invoke('EndCall', callId, targetUserId, durationSeconds, reason);
  }, [connection]);

  const addListener = useCallback((key, fn) => {
    listeners.current[key] = fn;
    return () => delete listeners.current[key];
  }, []);

  const addMessages = useCallback((convId, messages) => {
    setActiveMessages(prev => ({ ...prev, [convId]: messages }));
  }, []);

  const prependMessages = useCallback((convId, newMessages) => {
    setActiveMessages(prev => ({
      ...prev,
      [convId]: [...newMessages, ...(prev[convId] || [])]
    }));
  }, []);

  return (
    <ChatContext.Provider value={{
      connection, connected, conversations, setConversations, activeMessages, addMessages, prependMessages,
      sendMessage, markRead, startTyping, stopTyping, addListener, joinConversation,
      initiateCall, sendOffer, sendAnswer, sendIceCandidate, acceptCall, rejectCall, endCall
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
